import tokenStorage from './tokenStorage';
import AuthService from 'services/auth';
import { get } from 'utils/lodash';

interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

type TokenChangeCallback = (token: string | null) => void;

class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private subscribers: Set<TokenChangeCallback> = new Set();
  private refreshPromise: Promise<string | null> | null = null;
  private refreshTimeoutId: NodeJS.Timeout | null = null;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (typeof window !== 'undefined') {
      const storedAccessToken = tokenStorage.getAccessToken();
      const storedRefreshToken = tokenStorage.getRefreshToken();

      if (storedAccessToken) {
        this.accessToken = storedAccessToken;
        // Don't schedule refresh during initialization to avoid circular dependency
        // It will be scheduled when setTokens is called or on first ensureValidToken check
      }

      if (storedRefreshToken) {
        this.refreshToken = storedRefreshToken;
      }
    }
  }

  setTokens(access: string, refresh?: string): void {
    this.accessToken = access;
    tokenStorage.setAccessToken(access);

    if (refresh) {
      this.refreshToken = refresh;
      tokenStorage.setRefreshToken(refresh);
    }

    this.notifySubscribers();
    this.scheduleTokenRefresh();
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  subscribe(callback: TokenChangeCallback): () => void {
    this.subscribers.add(callback);
    callback(this.accessToken);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notifySubscribers(): void {
    this.subscribers.forEach(callback => {
      try {
        callback(this.accessToken);
      } catch (error) {
        console.error('Error in token subscriber:', error);
      }
    });
  }

  async refreshAccessToken(): Promise<string | null> {
    console.log('[TokenManager] refreshAccessToken called', {
      hasRefreshPromise: !!this.refreshPromise,
      hasRefreshToken: !!this.refreshToken,
      hasAccessToken: !!this.accessToken,
    });

    if (this.refreshPromise) {
      console.log('[TokenManager] Reusing existing refresh promise');
      return this.refreshPromise;
    }

    if (!this.refreshToken && !this.accessToken) {
      console.log('[TokenManager] No tokens available for refresh');
      return null;
    }

    this.refreshPromise = this.performTokenRefresh();

    try {
      const newToken = await this.refreshPromise;
      console.log('[TokenManager] Refresh completed', { success: !!newToken });
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<string | null> {
    try {
      const tokenToRefresh = this.refreshToken || this.accessToken;
      console.log('[TokenManager] performTokenRefresh starting', {
        hasTokenToRefresh: !!tokenToRefresh
      });

      if (!tokenToRefresh) {
        console.log('[TokenManager] No token to refresh');
        return null;
      }

      // Add timeout to prevent indefinite hanging
      const REFRESH_TIMEOUT = 10000; // 10 seconds
      const timeoutPromise = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('Token refresh timeout')), REFRESH_TIMEOUT);
      });

      const response = await Promise.race([
        AuthService.refreshToken(tokenToRefresh),
        timeoutPromise
      ]);

      console.log('[TokenManager] Refresh response received', {
        response: response,
      });

      // Check if response has errors (from BaseService error handling)
      if (response && 'errors' in response) {
        console.error('[TokenManager] Refresh failed with errors:', response.errors);
        this.clearTokens();
        // Redirect immediately on refresh failure
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return null;
      }

      const newAccessToken = get(response, 'data.refreshedToken');

      if (newAccessToken) {
        this.setTokens(newAccessToken, this.refreshToken || undefined);
        console.log('[TokenManager] New token set successfully');
        return newAccessToken;
      } else {
        console.log('[TokenManager] No new token in response, clearing tokens and redirecting');
        this.clearTokens();
        // Redirect immediately if no token received
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return null;
      }
    } catch (error) {
      console.error('[TokenManager] Token refresh failed:', error);
      this.clearTokens();
      // Redirect immediately on any error (including timeout)
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return null;
    }
  }

  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.accessToken;

    if (!tokenToCheck) return true;

    try {
      const payload = this.decodeToken(tokenToCheck);
      if (!payload || !payload.exp) return true;

      const currentTime = Date.now() / 1000;
      const bufferTime = 5 * 60;

      return currentTime >= (payload.exp - bufferTime);
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  }

  getTokenExpiry(token?: string): Date | null {
    const tokenToCheck = token || this.accessToken;

    if (!tokenToCheck) return null;

    try {
      const payload = this.decodeToken(tokenToCheck);
      if (!payload || !payload.exp) return null;

      return new Date(payload.exp * 1000);
    } catch (error) {
      console.error('Error getting token expiry:', error);
      return null;
    }
  }

  getTimeUntilExpiry(token?: string): number | null {
    const tokenToCheck = token || this.accessToken;

    if (!tokenToCheck) return null;

    try {
      const payload = this.decodeToken(tokenToCheck);
      if (!payload || !payload.exp) return null;

      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();

      return Math.max(0, expiryTime - currentTime);
    } catch (error) {
      console.error('Error calculating time until expiry:', error);
      return null;
    }
  }

  private decodeToken(token: string): TokenPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  clearTokens(): void {
    console.log('[TokenManager] Clearing tokens');
    this.accessToken = null;
    this.refreshToken = null;
    tokenStorage.clearTokens();
    this.cancelScheduledRefresh();
    this.notifySubscribers();

    // Also clear Zustand persisted storage to prevent rehydration loop
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('auth-storage');
        console.log('[TokenManager] Cleared persisted auth storage');
      } catch (error) {
        console.error('[TokenManager] Failed to clear persisted storage:', error);
      }
    }
  }

  private scheduleTokenRefresh(): void {
    this.cancelScheduledRefresh();

    if (!this.accessToken) return;

    const timeUntilExpiry = this.getTimeUntilExpiry();
    if (timeUntilExpiry === null || timeUntilExpiry <= 0) {
      this.refreshAccessToken();
      return;
    }

    const refreshTime = Math.max(0, timeUntilExpiry - 5 * 60 * 1000);

    if (refreshTime > 0) {
      this.refreshTimeoutId = setTimeout(() => {
        this.refreshAccessToken();
      }, refreshTime);
    } else {
      this.refreshAccessToken();
    }
  }

  private cancelScheduledRefresh(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  async ensureValidToken(): Promise<string | null> {
    // Schedule refresh if not already scheduled (for tokens loaded from storage)
    if (this.accessToken && !this.refreshTimeoutId) {
      this.scheduleTokenRefresh();
    }

    if (this.isTokenExpired()) {
      return await this.refreshAccessToken();
    }
    return this.accessToken;
  }

  getTokenDebugInfo(): {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    isExpired: boolean;
    expiryDate: Date | null;
    timeUntilExpiry: string;
    subscriberCount: number;
  } {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    let timeString = 'N/A';

    if (timeUntilExpiry !== null) {
      const hours = Math.floor(timeUntilExpiry / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilExpiry % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeUntilExpiry % (1000 * 60)) / 1000);
      timeString = `${hours}h ${minutes}m ${seconds}s`;
    }

    return {
      hasAccessToken: !!this.accessToken,
      hasRefreshToken: !!this.refreshToken,
      isExpired: this.isTokenExpired(),
      expiryDate: this.getTokenExpiry(),
      timeUntilExpiry: timeString,
      subscriberCount: this.subscribers.size,
    };
  }
}

export default new TokenManager();